import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import styled from "styled-components";
import { MdTheaters, MdCloudOff, MdExploreOff } from "react-icons/md";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import { apiGetEvents } from "../api/ticketingApi";
import { fetchTicketingConfig } from "../redux/ticketingSlice";
import { fmtDateTime, fmtMoney, minTicketPrice } from "../components/Ticketing/format";

// Sold out = no admission left on the event's next showing. Reserved seating
// compares seat counters; GA is sold out only when EVERY capped ticket type is
// exhausted (an uncapped type means unlimited, so never sold out).
const isSoldOut = (showing) => {
  if (!showing) return false;
  if (["RESERVED_SEATING", "RESERVED_UNITS"].includes(showing.inventoryModel)) {
    return Number(showing.seatsTotal) > 0 && Number(showing.seatsSold || 0) >= Number(showing.seatsTotal);
  }
  const types = showing.ticketTypes || [];
  if (!types.length) return false;
  return types.every((t) => t.gaCapacity != null && Number(t.gaSold || 0) >= Number(t.gaCapacity));
};

const EventsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const enabled = useSelector((s) => s.ticketing?.enabled);
  const configLoaded = useSelector((s) => s.ticketing?.configLoaded);
  const [events, setEvents] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Bumping this re-runs the effect below, which is how the retry button gets a
  // fresh fetch without duplicating the fetch/teardown logic.
  const [reloadKey, setReloadKey] = useState(0);

  // Resolve the pilot flag (fail-closed) so this page can say "not in your
  // market yet" instead of showing an empty catalog, which is what the backend
  // 404 would otherwise look like here.
  useEffect(() => {
    if (!configLoaded) dispatch(fetchTicketingConfig());
  }, [configLoaded, dispatch]);

  const load = useCallback((alive) => {
    Promise.all([apiGetEvents(), apiGetEvents({ view: "COMING_SOON" })])
      .then(([live, upcoming]) => alive.current && (setEvents(live.events || []), setComingSoon(upcoming.events || []), setError(""), setLoading(false)))
      .catch(() => alive.current && (setError("We couldn't load events right now."), setLoading(false)));
  }, []);

  useEffect(() => {
    const alive = { current: true };
    load(alive);

    // LIVE catalog — parity with the mobile apps: the backend emits
    // `ticketing:update` to the "ticketing" room whenever an event/showing is
    // created, published, or cancelled (and `type:"config"` when the
    // kill-switch flips). Reload so a showtime the ops team just activated
    // appears without a refresh.
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => socket.emit("join", "ticketing"));
    socket.on("ticketing:update", (msg) => {
      // Mobile already did this; web was reloading the list on a config flip
      // but never re-resolving the flag, so switching ticketing off left this
      // page reachable and rendering.
      if (msg && msg.type === "config") dispatch(fetchTicketingConfig());
      else load(alive);
    });

    return () => {
      alive.current = false;
      socket.off("ticketing:update");
      socket.disconnect();
    };
  }, [load, dispatch, reloadKey]);

  const retry = useCallback(() => {
    setLoading(true);
    setError("");
    setReloadKey((k) => k + 1);
  }, []);

  const featured = events.find((e) => e.organizer && e.organizer.bannerUrl);
  const heroBanner = featured && featured.organizer.bannerUrl;
  const heroOrg = featured && featured.organizer;

  return (
    <>
      <Navbar />
      <Page>
        <BackBar fallback="/" />
        {heroBanner && (
          <Hero style={{ backgroundImage: `url(${heroBanner})` }}>
            <HeroShade />
            {heroOrg && (
              <HeroTag>
                {heroOrg.logoUrl && <img src={heroOrg.logoUrl} alt="" />}
                <span>{heroOrg.name}</span>
              </HeroTag>
            )}
          </Hero>
        )}
        <Header>
          <h1>Events &amp; Tickets</h1>
          <p>Book seats for cinema, concerts and more — pay online, get a secure QR ticket.</p>
        </Header>

        {/* One state card instead of three bare lines of grey text. Each state
            says what happened and, where there is one, offers the next move —
            the page previously had no retry at all, so a failed fetch was a
            dead end until the visitor reloaded the browser. Mirrors the mobile
            EmptyState so the two surfaces read as the same product. */}
        {configLoaded && !enabled ? (
          <StateCard>
            <StateIcon $tone="neutral"><MdExploreOff /></StateIcon>
            <StateTitle>Not available here yet</StateTitle>
            <StateBody>
              Event ticketing hasn't launched in your area. We're rolling it out
              market by market — check back soon.
            </StateBody>
          </StateCard>
        ) : loading ? (
          <StateCard>
            <StateIcon><Spinner /></StateIcon>
            <StateTitle>Loading events…</StateTitle>
          </StateCard>
        ) : error ? (
          <StateCard>
            <StateIcon $tone="warn"><MdCloudOff /></StateIcon>
            <StateTitle>We couldn't load events</StateTitle>
            <StateBody>
              Check your connection and try again — nothing has gone wrong with
              your account.
            </StateBody>
            <StateCta type="button" onClick={retry}>
              Try again <span aria-hidden="true">→</span>
            </StateCta>
          </StateCard>
        ) : events.length === 0 && comingSoon.length === 0 ? (
          <StateCard>
            <StateIcon><MdTheaters /></StateIcon>
            <StateTitle>No events on sale yet</StateTitle>
            <StateBody>
              Cinema screenings, concerts and shows appear here the moment they
              go on sale — this page updates on its own, so there's no need to
              keep checking.
            </StateBody>
            <StateCta type="button" onClick={retry}>
              Refresh <span aria-hidden="true">→</span>
            </StateCta>
          </StateCard>
        ) : null}

        {events.length > 0 && <CatalogTitle><span>Now showing</span><h2>Book your next experience</h2></CatalogTitle>}
        <Grid>
          {events.map((ev) => {
            const price = ev.nextShowing ? minTicketPrice(ev.nextShowing) : null;
            const soldOut = isSoldOut(ev.nextShowing);
            return (
              <Card key={ev._id} onClick={() => history.push(`/event/${ev._id}`)} style={{ "--event-accent": ev.organizer?.theme?.primaryColor || "#83b524" }}>
                <Poster style={ev.posterUrl ? { backgroundImage: `url(${ev.posterUrl})` } : {}}>
                  {!ev.posterUrl && <Fallback>{(ev.title || "E").charAt(0)}</Fallback>}
                  <Badge>{ev.category}</Badge>
                  {soldOut && <SoldOut>Sold out</SoldOut>}
                </Poster>
                <Body>
                  <Title>{ev.title}</Title>
                  {ev.nextShowing && <Meta>{fmtDateTime(ev.nextShowing.startsAt)}</Meta>}
                  {soldOut ? <Price style={{ color: "#9ca3af" }}>Next showing sold out</Price>
                    : price != null && <Price>From {fmtMoney(price)}</Price>}
                </Body>
              </Card>
            );
          })}
        </Grid>
        {comingSoon.length > 0 && (
          <ComingSection>
            <CatalogTitle><span>Coming soon</span><h2>Plan ahead</h2></CatalogTitle>
            <Grid>
              {comingSoon.map((ev) => (
                <Card key={ev._id} onClick={() => history.push(`/event/${ev._id}`)} style={{ "--event-accent": ev.organizer?.theme?.primaryColor || "#83b524" }}>
                  <Poster style={ev.posterUrl ? { backgroundImage: `url(${ev.posterUrl})` } : {}}>
                    {!ev.posterUrl && <Fallback>{(ev.title || "E").charAt(0)}</Fallback>}
                    <Badge>COMING SOON</Badge>
                  </Poster>
                  <Body>
                    <Title>{ev.title}</Title>
                    <Meta>{ev.releaseDate ? `Opens ${new Date(ev.releaseDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` : "Showtimes to be announced"}</Meta>
                    {ev.trailerUrl && <Price>Trailer available</Price>}
                  </Body>
                </Card>
              ))}
            </Grid>
          </ComingSection>
        )}
      </Page>
    </>
  );
};

const Page = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: calc(var(--lusuku-nav-h, 130px) + 12px) 20px 48px;
  min-height: 60vh;
`;
const Hero = styled.div`
  position: relative;
  height: 200px;
  border-radius: 18px;
  background: #0d1a2f center/cover no-repeat;
  margin-bottom: 22px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.14);
  @media (max-width: 640px) { height: 140px; border-radius: 14px; margin-bottom: 16px; }
`;
const HeroShade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(13, 26, 47, 0.6), rgba(13, 26, 47, 0.05));
`;
const HeroTag = styled.div`
  position: absolute;
  left: 18px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  img { width: 40px; height: 40px; border-radius: 9px; object-fit: cover; border: 2px solid rgba(255, 255, 255, 0.85); background: #fff; }
  span { color: #fff; font-weight: 800; font-size: 16px; text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45); }
  @media (max-width: 640px) { left: 14px; bottom: 12px; img { width: 32px; height: 32px; } span { font-size: 14px; } }
`;
const Header = styled.div`
  margin-bottom: 22px;
  h1 {
    font-size: 26px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 6px;
  }
  p {
    color: #6b7280;
    margin: 0;
    font-size: 14px;
  }
`;
/* ── State card ────────────────────────────────────────────────────────────
   Deliberately the same shape as the mobile EmptyState (68px icon chip, 16px
   title, 13.5px body, green pill CTA) so /events reads the same on a phone
   browser and in the app. */
const StateCard = styled.div`
  max-width: 460px;
  margin: 40px auto 8px;
  background: #fff;
  border: 1px solid #edeff1;
  border-radius: 16px;
  padding: 34px 26px;
  text-align: center;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.07);
`;
const StateIcon = styled.div`
  width: 68px;
  height: 68px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.$tone === "warn" ? "#fdf1de" : p.$tone === "neutral" ? "#eef0f2" : "#eaf5d6"};
  svg {
    width: 32px;
    height: 32px;
    color: ${(p) =>
      p.$tone === "warn" ? "#a35c00" : p.$tone === "neutral" ? "#6b7280" : "#83b524"};
  }
`;
const Spinner = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 3px solid rgba(131, 181, 36, 0.25);
  border-top-color: #83b524;
  animation: lusuku-events-spin 0.8s linear infinite;
  @keyframes lusuku-events-spin {
    to { transform: rotate(360deg); }
  }
`;
const StateTitle = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #1f2937;
`;
const StateBody = styled.p`
  margin: 8px 0 0;
  font-size: 13.5px;
  line-height: 20px;
  color: #6b7280;
`;
const StateCta = styled.button`
  margin-top: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 10px;
  background: #83b524;
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
  padding: 12px 22px;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(77, 122, 16, 0.28);
  &:hover { background: #78a520; }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 18px;
`;
const CatalogTitle = styled.div`
  margin: 30px 0 14px;
  span { color: #83b524; font-size: 11px; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; }
  h2 { color: #1f2937; font-size: 21px; margin: 3px 0 0; }
`;
const ComingSection = styled.section`margin-top: 42px; padding-top: 6px; border-top: 1px solid #e5e7eb;`;
const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  }
`;
const Poster = styled.div`
  position: relative;
  height: 260px;
  background: #eef2e6 center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Fallback = styled.div`
  font-size: 64px;
  font-weight: 800;
  color: #b7cf8a;
`;
const Badge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: var(--event-accent, #83b524);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 4px 8px;
  border-radius: 6px;
`;
const SoldOut = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(31, 41, 55, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 4px 8px;
  border-radius: 6px;
`;
const Body = styled.div`
  padding: 12px 14px 16px;
`;
const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 6px;
`;
const Meta = styled.div`
  font-size: 12.5px;
  color: #6b7280;
`;
const Price = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--event-accent, #83b524);
`;

export default EventsPage;
